import { SupportedLanguage } from "../../../config/languages";
import { resolveLinkUrl } from "../../../helpers/linkHelpers";
import { Navigation } from "../../../payload-types";
import { Button } from "../../Button/Button";
import { Link } from "../../Link/Link";
import classNames from "classnames/bind";
import styles from "./MobileNavMenu.module.css";

const cx = classNames.bind(styles);

interface MobileNavMenuProps {
  isOpen: boolean;
  items?: Navigation["menuItems"];
  ctaButton?: Navigation["ctaButton"];
  submenu: string | null;
  onClose: () => void;
  onSubmenuChange: (submenu: string | null) => void;
  locale: SupportedLanguage;
}

export const MobileNavMenu = ({
  isOpen,
  items,
  ctaButton,
  submenu,
  onClose,
  onSubmenuChange,
  locale,
}: MobileNavMenuProps) => {
  const handleDropdownClick = (e: React.MouseEvent, label: string) => {
    e.preventDefault();
    onSubmenuChange(label);
  };

  const closeMobileSubmenu = () => {
    onSubmenuChange(null);
  };

  const handleLinkClick = () => {
    onClose();
  };

  return (
    <div className={cx("mobile-menu")} aria-expanded={isOpen}>
      <div className={cx("mobile-content")}>
        {!submenu ? (
          <>
            <ul className={cx("mobile-list")}>
              {items?.map((item, index) => {
                if (!item.label) return null;

                return (
                  <li key={item.label || index} className={cx("mobile-item")}>
                    {item.type === "dropdown" ? (
                      <button
                        className={cx("mobile-link", "dropdown-trigger")}
                        onClick={(e) => handleDropdownClick(e, item.label)}
                        data-dropdown={true}
                        data-open={submenu === item.label}
                        aria-expanded={submenu === item.label}
                        aria-haspopup="true"
                      >
                        {item.label}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M10.8828 10.0924L6.75806 5.96757L7.93656 4.78907L13.2399 10.0924L7.93657 15.3957L6.75806 14.2172L10.8828 10.0924Z"
                            fill="white"
                          />
                        </svg>
                      </button>
                    ) : (
                      <Link
                        href={resolveLinkUrl(item.linkSettings, "url") || ""}
                        className={cx("mobile-link")}
                        data-dropdown={false}
                        openInNewTab={item.linkSettings?.openInNewTab || false}
                        locale={locale}
                        onClick={handleLinkClick}
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
            {ctaButton && ctaButton.label && ctaButton.url && (
              <Button
                variant="primary"
                size="medium"
                fullWidth
                href={ctaButton.url}
                locale={locale}
              >
                {ctaButton.label}
              </Button>
            )}
          </>
        ) : (
          <div className={cx("mobile-submenu")}>
            <button
              className={cx("mobile-submenu-title")}
              onClick={closeMobileSubmenu}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M10.8828 10.0924L6.75806 5.96757L7.93656 4.78907L13.2399 10.0924L7.93657 15.3957L6.75806 14.2172L10.8828 10.0924Z"
                  fill="white"
                />
              </svg>
              {submenu}
            </button>
            {items
              ?.find((item) => item.label === submenu)
              ?.dropdownColumns?.map((column, columnIndex) => (
                <div key={columnIndex} className={cx("mobile-submenu-section")}>
                  {column.headerLink &&
                    (column.headerLink.url ? (
                      <Link
                        href={column.headerLink.url}
                        className={cx("mobile-submenu-header")}
                        locale={locale}
                        onClick={handleLinkClick}
                      >
                        {column.headerLink.label}
                      </Link>
                    ) : (
                      <h3 className={cx("mobile-submenu-header", "text")}>
                        {column.headerLink.label}
                      </h3>
                    ))}
                  {column.links && column.links.length > 0 && (
                    <ul className={cx("mobile-submenu-links")}>
                      {column.links.map((link, linkIndex) => (
                        <li key={linkIndex}>
                          <Link
                            href={resolveLinkUrl(link, "url") || ""}
                            className={cx("mobile-submenu-link")}
                            openInNewTab={link.openInNewTab || false}
                            locale={locale}
                            onClick={handleLinkClick}
                          >
                            <span className={cx("mobile-submenu-link-label")}>
                              {link.label}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
